// stores/helpers/uri.js
import { defineStore } from 'pinia';

export const useUriStore = defineStore('uri', {
  state: () => ({
    baseURL: (process.env.VUE_APP_HOST ? process.env.VUE_APP_HOST : '') + process.env.VUE_APP_API,
    env: 'env',
    i18n: 'i18n',
    getUserManager: 'user/manager',
    updateSchema: 'updateschema',
    getSchema: 'designschema',
    color: 'system/color',
    user: 'user',
    log: 'log',
    error: 'log/error',
    bankMaterialsAll: 'bank-materials/all',
    smsCode: 'session/sms-code',
    emailCode: 'session/email-code',
    registration: 'session/registration',
    login: 'session/login',
    loginAgent: 'login-agent',
    logout: 'session/logout',
    app: 'application',
    apps: 'applications',
    searchOrg: 'findByIdAndStatus',
    // searchOrg: 'suggest/org',
    searchAddr: 'suggest/address',
    searchCountry: 'suggest/country',
    version: 'version',
    createApplicationByPhysicalEntity: 'application/physical-entity',
    bankOffices: 'bankoffices',
    validateCaptcha: 'captcha',
    applicationResetClient: 'application/reset-client',
    marketing: 'marketing',
    ws: 'ws',
    signPepOffer: 'sign-pep-offer',
    downloadDefaultSignature: '/signature',
    downloadR: 'applications/download',
    pepOffert: 'sign-pep',
    applicationsCount: 'applications/count',
  }),

  getters: {
    // Геттеры просто возвращают значения из state
    logoLoginImg: (state) => state.baseURL + 'image/LOGIN_LOGO_LKK',
    headerLogoImage: (state) => state.baseURL + 'image/UPPER_MENU_LOGO_LKK',
    navbarImg: (state) => state.baseURL + 'image/NAVBAR',
    bgImg: (state) => state.baseURL + 'image/BG_MAIN_IMAGE_LKK',
    headerImg: (state) => state.baseURL + 'image/HEADER',
  },

  actions: {
    // Методы с параметрами

    bankMaterialsId(id: string) {
      return `bank-materials/${id}`;
    },

    appById(id: string) {
      return `${this.app}/${id}`;
    },

    form(id: string) {
      return `${this.app}/${id}/form`;
    },

    blankForm(id: string) {
      return `${this.app}/blank-form/${id}`;
    },

    blankFormStep1(id: string) {
      return `${this.app}/blank-form/first-step/${id}`;
    },

    updateForm(id: string) {
      return `${this.app}/${id}/update-form`;
    },

    annul(id: string) {
      return `${this.app}/${id}/annul`;
    },

    save(id: string) {
      return `${this.app}/update/${id}`;
    },

    status(id: string) {
      return `${this.app}/${id}/status`;
    },

    recognitionPassport(id: string) {
      return `${this.app}/${id ? id + '/' : ''}scanning/passport`;
    },

    participantOfApp(appId: string) {
      return `${this.app}/${appId}/participant`;
    },

    documents(id: string) {
      return `${this.app}/${id}/client-documents`;
    },

    document(id: string) {
      return `${this.app}/${id}/add-document`;
    },

    documentsTableStep4(id: string) {
      return `all-documents/${id}`;
    },

    addDocRequirement(reqId: string) {
      return `${this.app}/document-requirement/${reqId}/add-document`;
    },

    addDigitalDocument(appId: string) {
      return `${this.app}/${appId}/add-digital-document`;
    },

    decision(id: string) {
      return `${this.app}/${id}/decision`;
    },

    applicationSignBinary(uuids: string) {
      return 'application/' + uuids + '/sign';
    },

    urlSigning(appId: string, signatoryId: string, accessToken: string) {
      return (fileId: string) => `signatory/${fileId}/sign/${appId}/${signatoryId}?accessToken=${accessToken}`;
    },

    urlPep(appId: string, signatoryId: string, accessToken: string) {
      return (fileId: string) => `signatory/${fileId}/sign-pep/${appId}/${signatoryId}?accessToken=${accessToken}`;
    },

    downloadFileForSignatoryV2(appId: string, signatoryId: string, accessToken: string) {
      return (fileId: string) =>
        `/signatory/download-file-v2/${fileId}/${appId}/${signatoryId}?accessToken=${accessToken}`;
    },

    downloadFileForSignatory(appId: string, signatoryId: string, accessToken: string) {
      return (fileId: string) =>
        `/signatory/download-file/${fileId}/${appId}/${signatoryId}?accessToken=${accessToken}`;
    },

    downloadSignature(appId: string, signId: string, accessToken: string) {
      return `/signatory/download/${appId}/${signId}?accessToken=${accessToken}`;
    },

    downloadFileDocument(docId: string) {
      return 'file-download/' + docId;
    },

    generateDockLkk(appId: string) {
      return 'document-download/' + appId;
    },

    download(appId: string, docId: string) {
      return `${this.app}/${appId}/document/${docId}/download`;
    },

    // electronic docs
    electronicGenerate(appId: string) {
      return `electronic-document/${appId}/generate`;
    },

    metadataResultByTemplateId(appId: string, templateId: string) {
      return `electronic-document/${appId}/${templateId}/metadata`;
    },

    electronicGenerateMetaData(appId: string) {
      return `electronic-document/${appId}/generate-metadata`;
    },

    electronicSaveMetaData(appId: string, fileId: string) {
      return `electronic-document/${appId}/save-metadata/${fileId}`;
    },

    electronicSaveMetaDataByFileid(appId: string, fileId: string) {
      return `electronic-document/${appId}/metadata/${fileId}`;
    },

    downloadDocumentTemplate(templateId: string) {
      return `electronic-document/${this.app}/template-for-request/${templateId}`;
    },

    // step-4
    downloadFile(fileId: string) {
      return `/application/download-document-on-step-4/${fileId}`;
    },

    nextStatus(id: string) {
      return `${this.app}/${id}/status/next`;
    },

    meta(id: string) {
      return `${this.app}/${id}/meta`;
    },

    questionnaire(id: string, idN: string) {
      return `${this.app}/${id}/questionnaire-new?${idN}`;
    },

    resetClient(id: string) {
      return `${this.app}/reset-client/${id}`;
    },

    signatory(appId: string, signatoryId: string) {
      return `signatory/${appId}/${signatoryId}`;
    },

    signatoryBank(appId: string, signatoryId: string) {
      return `signatory/${appId}/bank/${signatoryId}`;
    },

    signedDocTypes(appId: string) {
      return `generate-docs/${appId}/signed-doc-type`;
    },

    participantSignatories(appId: string) {
      return `generate-docs/${appId}/signed-participant`;
    },

    dynamicDictionary(appId: string) {
      return `generate-docs/${appId}/value-dictionary`;
    },

    generateDocs(appId: string) {
      return `generate-docs/${appId}/form`;
    },

    bankSignatoriesByOfficeId(officeId: string) {
      return `users/signed-bank/${officeId}`;
    },

    documentTypeMchd(appId: string) {
      return `document-type/mchd/${appId}`;
    },
  },
});
